import { isInstitutionalRules } from '@urbanik/api-contracts';
export class InstitutionalRuleWriteError extends Error {
    status;
    constructor(status, message) {
        super(message ??
            (status === 403
                ? 'No tienes permiso para crear reglas en esta entidad.'
                : status === 404
                    ? 'El concepto o el acta no están disponibles en esta entidad.'
                    : 'No se pudo guardar la regla.'));
        this.status = status;
    }
}
export async function createInstitutionalRule(base, org, input, signal) {
    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(org))
        throw new InstitutionalRuleWriteError(400);
    let response;
    try {
        response = await fetch(base.replace(/\/$/, '') + '/identity/multi-review/organizations/' + org + '/governance/rules', {
            method: 'POST',
            credentials: 'include',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json', 'X-Urbanik-Review': '1' },
            body: JSON.stringify(input),
            signal: signal ?? null,
        });
    }
    catch (error) {
        if (signal?.aborted)
            throw error;
        throw new InstitutionalRuleWriteError(503);
    }
    let data;
    try {
        data = await response.json();
    }
    catch {
        throw new InstitutionalRuleWriteError(response.ok ? 502 : response.status);
    }
    if (!response.ok) {
        const message = response.status === 400 &&
            typeof data === 'object' &&
            data !== null &&
            'message' in data &&
            typeof data.message === 'string'
            ? data.message
            : undefined;
        throw new InstitutionalRuleWriteError(response.status, message);
    }
    if (!isInstitutionalRules(data, org) || data.items.length !== 1)
        throw new InstitutionalRuleWriteError(502);
    const item = data.items[0];
    if (item.versionNumber !== 1 ||
        !item.isCurrent ||
        item.deletedAt !== null ||
        item.conceptId !== input.conceptId ||
        item.actId !== input.actId ||
        item.amount !== input.amount ||
        item.periodicity !== input.periodicity ||
        item.effectiveFrom !== input.effectiveFrom ||
        item.effectiveTo !== input.effectiveTo)
        throw new InstitutionalRuleWriteError(502);
    return item;
}
export async function createInstitutionalRuleVersion(base, org, id, input, signal) {
    const result = await writeRule(base, org, id + '/versions', input, signal, 201);
    if (result.id === id ||
        result.lineageId.length < 1 ||
        result.versionNumber !== input.expectedVersionNumber + 1 ||
        !result.isCurrent ||
        result.deletedAt !== null ||
        result.conceptId !== input.conceptId ||
        result.actId !== input.actId ||
        result.amount !== input.amount)
        throw new InstitutionalRuleWriteError(502);
    return result;
}
export async function setInstitutionalRuleLifecycle(base, org, id, lineageId, expectedVersionNumber, annulled, reason, signal) {
    const result = await writeRule(base, org, id + '/lifecycle', { annulled, expectedVersionNumber, reason }, signal, 200);
    if (result.id !== id ||
        result.lineageId !== lineageId ||
        result.versionNumber !== expectedVersionNumber ||
        !!result.deletedAt !== annulled)
        throw new InstitutionalRuleWriteError(502);
    return result;
}
async function writeRule(base, org, path, body, signal, status) {
    if (!/^[0-9a-f-]{36}$/.test(org) || !/^[0-9a-f-]{36}(?:\/|$)/.test(path))
        throw new InstitutionalRuleWriteError(400);
    let response;
    try {
        response = await fetch(base.replace(/\/$/, '') +
            '/identity/multi-review/organizations/' +
            org +
            '/governance/rules/' +
            path, {
            method: 'POST',
            credentials: 'include',
            cache: 'no-store',
            headers: { 'Content-Type': 'application/json', 'X-Urbanik-Review': '1' },
            body: JSON.stringify(body),
            signal: signal ?? null,
        });
    }
    catch (error) {
        if (signal?.aborted)
            throw error;
        throw new InstitutionalRuleWriteError(503);
    }
    let data;
    try {
        data = await response.json();
    }
    catch {
        throw new InstitutionalRuleWriteError(response.ok ? 502 : response.status);
    }
    if (!response.ok)
        throw new InstitutionalRuleWriteError(response.status, response.status === 409 ? 'La regla cambió; actualiza antes de continuar.' : undefined);
    if (response.status !== status || !isInstitutionalRules(data, org) || data.items.length !== 1)
        throw new InstitutionalRuleWriteError(502);
    return data.items[0];
}
